import React, { useRef, useEffect } from 'react';
import { setupCanvas } from './canvasUtils';

// ============================================
// Chapter 3  -  The Speed of Light
// ============================================

/** 3.2 – Light Invariance: Same speed for all observers */
export const LightInvariance = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 300;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('The Speed of Light Is the Same for All Observers', W / 2, 24);

        // Scene 1: Stationary observer
        const sy = 100;
        ctx.fillStyle = '#E0F2FE'; ctx.beginPath(); ctx.roundRect(30, sy - 35, 240, 70, 10); ctx.fill();
        ctx.strokeStyle = '#93C5FD'; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.roundRect(30, sy - 35, 240, 70, 10); ctx.stroke();

        // Person standing
        ctx.fillStyle = '#3B82F6'; ctx.beginPath(); ctx.arc(60, sy - 10, 12, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = '#3B82F6'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(60, sy + 2); ctx.lineTo(60, sy + 22); ctx.stroke();

        // Light beam
        ctx.strokeStyle = '#FCD34D'; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(90, sy); ctx.lineTo(250, sy); ctx.stroke();
        ctx.fillStyle = '#FCD34D'; ctx.beginPath(); ctx.moveTo(250, sy); ctx.lineTo(243, sy - 5); ctx.lineTo(243, sy + 5); ctx.closePath(); ctx.fill();

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('Stationary observer', 80, sy - 22);
        ctx.fillStyle = '#EAB308'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Measures: c', 170, sy + 20);

        // Scene 2: Moving observer
        const my = 200;
        ctx.fillStyle = '#FEF3C7'; ctx.beginPath(); ctx.roundRect(30, my - 35, 240, 70, 10); ctx.fill();
        ctx.strokeStyle = '#FCD34D'; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.roundRect(30, my - 35, 240, 70, 10); ctx.stroke();

        // Person in moving car
        ctx.fillStyle = '#F59E0B'; ctx.beginPath(); ctx.arc(60, my - 10, 12, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = '#F59E0B'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(60, my + 2); ctx.lineTo(60, my + 22); ctx.stroke();
        // Speed arrow
        ctx.fillStyle = '#F59E0B'; ctx.font = 'bold 14px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('→ 0.9c', 40, my + 36);

        // Same light beam
        ctx.strokeStyle = '#FCD34D'; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(90, my); ctx.lineTo(250, my); ctx.stroke();
        ctx.fillStyle = '#FCD34D'; ctx.beginPath(); ctx.moveTo(250, my); ctx.lineTo(243, my - 5); ctx.lineTo(243, my + 5); ctx.closePath(); ctx.fill();

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('Moving at 0.9c', 80, my - 22);
        ctx.fillStyle = '#EAB308'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Still measures: c', 170, my + 20);

        // Right side: key insight
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 14px Inter,sans-serif'; ctx.textAlign = 'center';
        const kx = 420;
        ctx.fillText('NOT 1.9c!', kx, 120);
        ctx.fillStyle = '#EF4444'; ctx.font = 'bold 28px Inter,sans-serif';
        ctx.fillText('c = c', kx, 160);
        ctx.fillStyle = '#64748B'; ctx.font = '12px Inter,sans-serif';
        ctx.fillText("Light's speed does", kx, 195);
        ctx.fillText('not add to the', kx, 212);
        ctx.fillText("observer's speed", kx, 229);

        // Bottom
        ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('c = 299,792,458 m/s  -  the same in every inertial reference frame', W / 2, H - 12);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-amber-50 to-blue-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={300} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 3.3 – Michelson-Morley Interferometer */
export const MichelsonInterferometer = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 340;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Michelson-Morley Interferometer (1887)', W / 2, 24);

        const bx = 200, by = 160; // beam splitter location

        // Beam splitter (45° square)
        ctx.save(); ctx.translate(bx, by); ctx.rotate(Math.PI / 4);
        ctx.fillStyle = '#93C5FD'; ctx.fillRect(-12, -12, 24, 24);
        ctx.strokeStyle = '#3B82F6'; ctx.lineWidth = 2; ctx.strokeRect(-12, -12, 24, 24);
        ctx.restore();
        ctx.fillStyle = '#3B82F6'; ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Beam', bx, by + 30); ctx.fillText('Splitter', bx, by + 43);

        // Light source (left)
        ctx.fillStyle = '#FCD34D';
        ctx.beginPath(); ctx.arc(60, by, 15, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = '#92400E'; ctx.font = 'bold 10px Inter,sans-serif'; ctx.fillText('Light', 60, by + 4);
        ctx.strokeStyle = '#FCD34D'; ctx.lineWidth = 2.5;
        ctx.beginPath(); ctx.moveTo(75, by); ctx.lineTo(bx - 18, by); ctx.stroke();

        // Mirror 1 (right)
        const m1x = 400;
        ctx.fillStyle = '#CBD5E1'; ctx.fillRect(m1x, by - 15, 8, 30);
        ctx.strokeStyle = '#64748B'; ctx.lineWidth = 1.5; ctx.strokeRect(m1x, by - 15, 8, 30);
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Mirror 1', m1x + 4, by + 38);

        // Arm 1 (horizontal beam to mirror)
        ctx.strokeStyle = '#EF4444'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(bx + 18, by); ctx.lineTo(m1x, by); ctx.stroke();
        // Return beam (offset slightly)
        ctx.strokeStyle = '#EF444480'; ctx.setLineDash([4, 3]);
        ctx.beginPath(); ctx.moveTo(m1x, by + 4); ctx.lineTo(bx + 14, by + 4); ctx.stroke();
        ctx.setLineDash([]);

        // Mirror 2 (top)
        const m2y = 50;
        ctx.fillStyle = '#CBD5E1'; ctx.fillRect(bx - 15, m2y, 30, 8);
        ctx.strokeStyle = '#64748B'; ctx.lineWidth = 1.5; ctx.strokeRect(bx - 15, m2y, 30, 8);
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('Mirror 2', bx, m2y - 8);

        // Arm 2 (vertical beam to mirror)
        ctx.strokeStyle = '#22C55E'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(bx, by - 18); ctx.lineTo(bx, m2y + 8); ctx.stroke();
        ctx.strokeStyle = '#22C55E80'; ctx.setLineDash([4, 3]);
        ctx.beginPath(); ctx.moveTo(bx + 4, m2y + 8); ctx.lineTo(bx + 4, by - 14); ctx.stroke();
        ctx.setLineDash([]);

        // Detector (bottom-right from splitter)
        ctx.fillStyle = '#E2E8F0'; ctx.beginPath(); ctx.roundRect(bx - 25, by + 60, 50, 30, 6); ctx.fill();
        ctx.strokeStyle = '#94A3B8'; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.roundRect(bx - 25, by + 60, 50, 30, 6); ctx.stroke();
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('Detector', bx, by + 80);

        // Result box (right side)
        ctx.fillStyle = '#FEF2F2'; ctx.beginPath(); ctx.roundRect(320, 50, 210, 90, 10); ctx.fill();
        ctx.strokeStyle = '#FECACA'; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.roundRect(320, 50, 210, 90, 10); ctx.stroke();
        ctx.fillStyle = '#DC2626'; ctx.font = 'bold 13px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Result: No fringe shift!', 425, 78);
        ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif';
        ctx.fillText('No evidence of "aether"', 425, 100);
        ctx.fillText('→ Speed of light is constant', 425, 118);

        // Bottom
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('If light traveled through an "aether", the arms would show different travel times', W / 2, H - 28);
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 11px Inter,sans-serif';
        ctx.fillText('→ This null result helped inspire Einstein\'s Special Relativity', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-cyan-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={320} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 3.4 – Lorentz Factor γ vs v/c */
export const LorentzFactorCurve = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 540, H = 300;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        const m = { top: 40, right: 30, bottom: 45, left: 55 };
        const pw = W - m.left - m.right, ph = H - m.top - m.bottom;

        // Title
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Lorentz Factor γ vs Speed', W / 2, 24);

        // Axes
        ctx.strokeStyle = '#1E293B'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(m.left, m.top); ctx.lineTo(m.left, m.top + ph); ctx.lineTo(m.left + pw, m.top + ph); ctx.stroke();

        // Y-axis label
        ctx.save(); ctx.translate(18, m.top + ph / 2); ctx.rotate(-Math.PI / 2);
        ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif'; ctx.textAlign = 'center'; ctx.fillText('γ = 1/√(1−β²)', 0, 0);
        ctx.restore();

        // X-axis label
        ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('v / c (β)', m.left + pw / 2, H - 6);

        // Grid
        const maxGamma = 12;
        for (let g = 2; g <= maxGamma; g += 2) {
            const y = m.top + ph - (g / maxGamma) * ph;
            ctx.strokeStyle = '#F1F5F9'; ctx.lineWidth = 1;
            ctx.beginPath(); ctx.moveTo(m.left, y); ctx.lineTo(m.left + pw, y); ctx.stroke();
            ctx.fillStyle = '#94A3B8'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'right';
            ctx.fillText(g.toString(), m.left - 8, y + 4);
        }
        for (let b = 0.2; b <= 1; b += 0.2) {
            const x = m.left + b * pw;
            ctx.strokeStyle = '#F1F5F9'; ctx.lineWidth = 1;
            ctx.beginPath(); ctx.moveTo(x, m.top); ctx.lineTo(x, m.top + ph); ctx.stroke();
            ctx.fillStyle = '#94A3B8'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(b.toFixed(1), x, m.top + ph + 15);
        }

        // γ curve
        ctx.beginPath(); ctx.strokeStyle = '#3B82F6'; ctx.lineWidth = 3;
        for (let px = 0; px <= pw - 5; px++) {
            const beta = (px / pw) * 0.995;
            const gamma = 1 / Math.sqrt(1 - beta * beta);
            const gClamped = Math.min(gamma, maxGamma + 1);
            const x = m.left + px;
            const y = m.top + ph - (gClamped / maxGamma) * ph;
            if (px === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }
        ctx.stroke();

        // Annotated points
        const annotations = [
            { beta: 0.5, label: 'γ=1.15', desc: '50% of c' },
            { beta: 0.87, label: 'γ=2.0', desc: '87% of c' },
            { beta: 0.99, label: 'γ=7.1', desc: '99% of c' },
        ];
        annotations.forEach(a => {
            const gamma = 1 / Math.sqrt(1 - a.beta * a.beta);
            const x = m.left + a.beta * pw;
            const y = m.top + ph - (Math.min(gamma, maxGamma) / maxGamma) * ph;
            ctx.beginPath(); ctx.arc(x, y, 4, 0, Math.PI * 2); ctx.fillStyle = '#EF4444'; ctx.fill();
            ctx.fillStyle = '#1E293B'; ctx.font = 'bold 10px Inter,sans-serif'; ctx.textAlign = 'left';
            ctx.fillText(a.label, x + 6, y - 2);
            ctx.fillStyle = '#94A3B8'; ctx.font = '9px Inter,sans-serif';
            ctx.fillText(a.desc, x + 6, y + 10);
        });

        // Asymptote label
        ctx.fillStyle = '#EF4444'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'right';
        ctx.fillText('γ → ∞ as v → c', m.left + pw - 5, m.top + 18);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={540} height={300} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 3.5 – Refraction & Refractive Indices */
export const RefractionMedia = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 520, H = 300;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Light in Different Media', W / 2, 24);

        // Refraction diagram (left side)
        const dx = 160, dy = 160;
        // Air region (top)
        ctx.fillStyle = '#F0F9FF'; ctx.fillRect(30, 45, 260, 115);
        ctx.fillStyle = '#64748B'; ctx.font = '12px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('Air (n = 1.0003)', 40, 62);

        // Water region (bottom)
        ctx.fillStyle = '#DBEAFE'; ctx.fillRect(30, 160, 260, 115);
        ctx.fillStyle = '#64748B'; ctx.fillText('Water (n = 1.33)', 40, 260);

        // Interface line
        ctx.strokeStyle = '#94A3B8'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(30, 160); ctx.lineTo(290, 160); ctx.stroke();

        // Normal (dashed vertical)
        ctx.strokeStyle = '#CBD5E1'; ctx.lineWidth = 1; ctx.setLineDash([4, 3]);
        ctx.beginPath(); ctx.moveTo(dx, 55); ctx.lineTo(dx, 265); ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = '#94A3B8'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('Normal', dx + 4, 58);

        // Incident ray (steeper angle)
        ctx.strokeStyle = '#FCD34D'; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(90, 60); ctx.lineTo(dx, 160); ctx.stroke();

        // Refracted ray (shallower angle  -  toward normal in denser medium)
        ctx.beginPath(); ctx.moveTo(dx, 160); ctx.lineTo(220, 265); ctx.stroke();

        // Angle arcs
        ctx.strokeStyle = '#EF4444'; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.arc(dx, 160, 30, -Math.PI / 2, -Math.PI / 2 + 0.75, false); ctx.stroke();
        ctx.fillStyle = '#EF4444'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('θ₁', dx + 12, 138);

        ctx.strokeStyle = '#3B82F6'; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.arc(dx, 160, 30, Math.PI / 2 - 0.5, Math.PI / 2, false); ctx.stroke();
        ctx.fillStyle = '#3B82F6'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('θ₂', dx + 8, 196);

        // Right side: refractive index table
        const tx = 340, ty = 55;
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 13px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Refractive Indices', tx + 80, ty);

        const media = [
            { name: 'Vacuum', n: '1.0000', col: '#94A3B8' },
            { name: 'Air', n: '1.0003', col: '#64748B' },
            { name: 'Water', n: '1.333', col: '#3B82F6' },
            { name: 'Glass', n: '1.52', col: '#22C55E' },
            { name: 'Diamond', n: '2.42', col: '#8B5CF6' },
        ];
        media.forEach((m, i) => {
            const y = ty + 22 + i * 28;
            ctx.fillStyle = m.col + '15'; ctx.beginPath(); ctx.roundRect(tx, y, 160, 22, 4); ctx.fill();
            ctx.fillStyle = '#1E293B'; ctx.font = '12px Inter,sans-serif'; ctx.textAlign = 'left';
            ctx.fillText(m.name, tx + 10, y + 16);
            ctx.fillStyle = m.col; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'right';
            ctx.fillText('n = ' + m.n, tx + 150, y + 16);
        });

        // Snell's law
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 13px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText("Snell's Law:", tx + 80, ty + 180);
        ctx.fillStyle = '#475569'; ctx.font = '14px Inter,sans-serif';
        ctx.fillText('n₁ sin θ₁ = n₂ sin θ₂', tx + 80, ty + 200);

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Phase velocity in medium: v = c/n  -  particles exceeding this emit Cherenkov light', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={520} height={300} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 3.6 – Causality Diagram (why FTL is impossible) */
export const CausalityDiagram = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 500, H = 300;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Why Faster-Than-Light Signals Break Causality', W / 2, 24);

        const cx = W / 2, cy = 165, ax = 100;

        // Light cone fill
        ctx.fillStyle = '#FEF3C720';
        ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(cx - ax, cy - ax); ctx.lineTo(cx + ax, cy - ax); ctx.closePath(); ctx.fill();
        ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(cx - ax, cy + ax); ctx.lineTo(cx + ax, cy + ax); ctx.closePath(); ctx.fill();

        // Light cone lines
        ctx.strokeStyle = '#FCD34D'; ctx.lineWidth = 2; ctx.setLineDash([5, 3]);
        ctx.beginPath(); ctx.moveTo(cx - ax, cy - ax); ctx.lineTo(cx, cy); ctx.lineTo(cx + ax, cy - ax); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(cx - ax, cy + ax); ctx.lineTo(cx, cy); ctx.lineTo(cx + ax, cy + ax); ctx.stroke();
        ctx.setLineDash([]);

        // Axes
        ctx.strokeStyle = '#1E293B'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(cx, cy + ax + 10); ctx.lineTo(cx, cy - ax - 10); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(cx - ax - 20, cy); ctx.lineTo(cx + ax + 20, cy); ctx.stroke();
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'right';
        ctx.fillText('ct', cx - 6, cy - ax - 5);
        ctx.textAlign = 'left'; ctx.fillText('x', cx + ax + 8, cy - 5);

        // Labels
        ctx.fillStyle = '#F59E0B'; ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('FUTURE', cx, cy - ax + 18);
        ctx.fillText('PAST', cx, cy + ax - 8);
        ctx.fillStyle = '#94A3B8'; ctx.font = '10px Inter,sans-serif';
        ctx.fillText('Spacelike', cx - ax + 15, cy - 5);
        ctx.fillText('Spacelike', cx + ax - 15, cy - 5);
        ctx.fillStyle = '#F59E0B'; ctx.font = 'italic 10px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('v = c', cx + ax - 30, cy - ax + 8);

        // FTL signal (crosses light cone  -  goes into spacelike region)
        ctx.strokeStyle = '#EF4444'; ctx.lineWidth = 2.5; ctx.setLineDash([6, 3]);
        ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(cx + ax + 10, cy - 30); ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = '#EF4444'; ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('FTL signal', cx + ax - 20, cy - 38);
        ctx.fillStyle = '#EF4444'; ctx.font = '10px Inter,sans-serif';
        ctx.fillText('(forbidden!)', cx + ax - 20, cy - 25);

        // Causal signal (inside light cone)
        ctx.strokeStyle = '#22C55E'; ctx.lineWidth = 2.5;
        ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(cx + 40, cy - 80); ctx.stroke();
        ctx.fillStyle = '#22C55E'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('Causal signal', cx + 44, cy - 75);
        ctx.fillText('(v < c, OK)', cx + 44, cy - 62);

        // Origin dot
        ctx.beginPath(); ctx.arc(cx, cy, 4, 0, Math.PI * 2); ctx.fillStyle = '#1E293B'; ctx.fill();

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Signals can only travel within the light cone  -  causality requires v ≤ c', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-amber-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={500} height={300} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 3.10 – Speed of Light Fact Sheet */
export const SpeedOfLightFactSheet = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 280;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        const cards = [
            { title: 'c (exact)', value: '299,792,458 m/s', desc: 'Defined since 1983', color: '#3B82F6' },
            { title: 'β = v/c', value: '0 to 1', desc: 'Speed as fraction of c', color: '#22C55E' },
            { title: 'γ = 1/√(1−β²)', value: '1 to ∞', desc: 'Lorentz factor', color: '#F59E0B' },
            { title: 'Cherenkov', value: 'β > 1/n', desc: 'Light in medium threshold', color: '#8B5CF6' },
        ];

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Speed of Light  -  Key Facts', W / 2, 28);

        cards.forEach((card, i) => {
            const x = 20 + (i % 2) * 270, y = 48 + Math.floor(i / 2) * 112;
            const cw = 252, ch = 98;

            ctx.fillStyle = card.color + '10'; ctx.beginPath(); ctx.roundRect(x, y, cw, ch, 12); ctx.fill();
            ctx.strokeStyle = card.color; ctx.lineWidth = 2; ctx.beginPath(); ctx.roundRect(x, y, cw, ch, 12); ctx.stroke();

            ctx.fillStyle = card.color; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'left';
            ctx.fillText(card.title, x + 14, y + 22);
            ctx.fillStyle = '#1E293B'; ctx.font = 'bold 18px Inter,sans-serif';
            ctx.fillText(card.value, x + 14, y + 52);
            ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
            ctx.fillText(card.desc, x + 14, y + 74);
        });
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-indigo-50 to-slate-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={280} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};


// ============================================
// Chapter 4  -  Special Relativity Intro
// ============================================

/** 4.2 – Muon Time Dilation: Lab vs Muon Frame */
export const MuonTimeDilation = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 320;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Cosmic Muons: Time Dilation in Action', W / 2, 24);

        // Left: Without relativity
        const lx = 140, rx = 420;
        ctx.fillStyle = '#FEE2E2'; ctx.beginPath(); ctx.roundRect(20, 52, 240, 250, 12); ctx.fill();
        ctx.strokeStyle = '#FECACA'; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.roundRect(20, 52, 240, 250, 12); ctx.stroke();
        ctx.fillStyle = '#DC2626'; ctx.font = 'bold 13px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Without Relativity', lx, 74);

        // Atmosphere column
        ctx.fillStyle = '#DBEAFE50'; ctx.fillRect(lx - 15, 90, 30, 190);
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('15 km', lx - 35, 184); ctx.fillText('atmosphere', lx + 45, 184);

        // Muon starts
        ctx.beginPath(); ctx.arc(lx, 108, 8, 0, Math.PI * 2); ctx.fillStyle = '#8B5CF6'; ctx.fill();
        ctx.fillStyle = '#8B5CF6'; ctx.font = '11px Inter,sans-serif'; ctx.fillText('μ born', lx, 98);

        // Muon decays early (at ~660m)
        ctx.beginPath(); ctx.arc(lx, 142, 6, 0, Math.PI * 2); ctx.fillStyle = '#EF4444'; ctx.fill();
        ctx.fillStyle = '#EF4444'; ctx.font = 'bold 10px Inter,sans-serif'; ctx.fillText('�- Decays at 660 m', lx + 22, 146);

        // Ground
        ctx.fillStyle = '#86EFAC'; ctx.fillRect(lx - 30, 280, 60, 6);
        ctx.fillStyle = '#22C55E'; ctx.font = '10px Inter,sans-serif'; ctx.fillText('Ground', lx, 300);

        ctx.fillStyle = '#DC2626'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('τ = 2.2 μs', lx, 248);
        ctx.fillText('cτ ≈ 660 m only', lx, 262);

        // Right: With relativity
        ctx.fillStyle = '#F0FDF4'; ctx.beginPath(); ctx.roundRect(300, 52, 240, 250, 12); ctx.fill();
        ctx.strokeStyle = '#BBF7D0'; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.roundRect(300, 52, 240, 250, 12); ctx.stroke();
        ctx.fillStyle = '#16A34A'; ctx.font = 'bold 13px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('With Relativity (γ ≈ 40)', rx, 74);

        // Atmosphere column
        ctx.fillStyle = '#DBEAFE50'; ctx.fillRect(rx - 15, 90, 30, 190);

        // Muon survives to ground
        ctx.beginPath(); ctx.arc(rx, 108, 8, 0, Math.PI * 2); ctx.fillStyle = '#8B5CF6'; ctx.fill();
        ctx.fillStyle = '#8B5CF6'; ctx.font = '11px Inter,sans-serif'; ctx.fillText('μ born', rx, 98);

        // Arrow all the way down
        ctx.strokeStyle = '#22C55E'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(rx, 118); ctx.lineTo(rx, 276); ctx.stroke();
        ctx.fillStyle = '#22C55E'; ctx.beginPath(); ctx.moveTo(rx, 280); ctx.lineTo(rx - 5, 272); ctx.lineTo(rx + 5, 272); ctx.closePath(); ctx.fill();

        ctx.beginPath(); ctx.arc(rx, 280, 8, 0, Math.PI * 2); ctx.fillStyle = '#22C55E'; ctx.fill();
        ctx.fillStyle = 'white'; ctx.font = 'bold 10px Inter,sans-serif'; ctx.fillText('✓', rx, 263);

        ctx.fillStyle = '#86EFAC'; ctx.fillRect(rx - 30, 280, 60, 6);
        ctx.fillStyle = '#22C55E'; ctx.font = '10px Inter,sans-serif'; ctx.fillText('Reaches ground!', rx, 304);

        ctx.fillStyle = '#16A34A'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('τ_lab = γτ ≈ 88 μs', rx, 248);
        ctx.fillText('d = βcγτ ≈ 26 km', rx, 262);

        // Bottom
        ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Time dilation stretches the muon lifetime by γ ≈ 40  -  it reaches the ground', W / 2, H - 12);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-purple-50 to-green-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={320} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 4.3 – Twin Paradox Worldlines */
export const TwinParadoxWorldlines = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 520, H = 320;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('The Twin Paradox', W / 2, 24);

        const cx = 210, bottom = 275, top = 55;

        // ct axis
        ctx.strokeStyle = '#1E293B'; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(cx, bottom + 5); ctx.lineTo(cx, top - 10); ctx.stroke();
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'right';
        ctx.fillText('ct', cx - 6, top - 5);

        // x axis
        ctx.beginPath(); ctx.moveTo(cx - 120, bottom); ctx.lineTo(cx + 180, bottom); ctx.stroke();
        ctx.fillStyle = '#1E293B'; ctx.textAlign = 'left'; ctx.fillText('x', cx + 165, bottom - 8);

        // Twin A: stays home (vertical worldline)
        ctx.strokeStyle = '#3B82F6'; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(cx, bottom); ctx.lineTo(cx, top); ctx.stroke();
        ctx.fillStyle = '#3B82F6'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'right';
        ctx.fillText('Twin A', cx - 8, (top + bottom) / 2);
        ctx.fillText('(stays)', cx - 8, (top + bottom) / 2 + 16);

        // Twin B: travels out and back (V-shape)
        const turnX = cx + 90, turnY = (top + bottom) / 2;
        ctx.strokeStyle = '#EF4444'; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(cx, bottom); ctx.lineTo(turnX, turnY); ctx.lineTo(cx, top); ctx.stroke();
        ctx.fillStyle = '#EF4444'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('Twin B', turnX + 8, turnY - 5);
        ctx.fillText('(travels)', turnX + 8, turnY + 11);

        // Turnaround point
        ctx.beginPath(); ctx.arc(turnX, turnY, 5, 0, Math.PI * 2); ctx.fillStyle = '#EF4444'; ctx.fill();
        ctx.fillStyle = '#94A3B8'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Turnaround', turnX, turnY + 25);

        // Start and end points
        ctx.beginPath(); ctx.arc(cx, bottom, 5, 0, Math.PI * 2); ctx.fillStyle = '#1E293B'; ctx.fill();
        ctx.beginPath(); ctx.arc(cx, top, 5, 0, Math.PI * 2); ctx.fillStyle = '#1E293B'; ctx.fill();
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Depart', cx, bottom + 16);
        ctx.fillText('Reunite', cx, top - 10);

        // Age labels (right side boxes)
        ctx.fillStyle = '#EFF6FF'; ctx.beginPath(); ctx.roundRect(370, 90, 120, 70, 8); ctx.fill();
        ctx.strokeStyle = '#BFDBFE'; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.roundRect(370, 90, 120, 70, 8); ctx.stroke();
        ctx.fillStyle = '#3B82F6'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Twin A aged:', 430, 112);
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 18px Inter,sans-serif';
        ctx.fillText('20 years', 430, 140);

        ctx.fillStyle = '#FEF2F2'; ctx.beginPath(); ctx.roundRect(370, 175, 120, 70, 8); ctx.fill();
        ctx.strokeStyle = '#FECACA'; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.roundRect(370, 175, 120, 70, 8); ctx.stroke();
        ctx.fillStyle = '#EF4444'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Twin B aged:', 430, 197);
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 18px Inter,sans-serif';
        ctx.fillText('~17 years', 430, 225);

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('The traveling twin ages less  -  acceleration breaks the symmetry', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-red-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={520} height={320} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 4.4 – Length Contraction */
export const LengthContraction = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 240;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Length Contraction', W / 2, 24);

        // At rest
        const y1 = 80, trainL = 320;
        ctx.fillStyle = '#DBEAFE'; ctx.beginPath(); ctx.roundRect(80, y1, trainL, 40, 8); ctx.fill();
        ctx.strokeStyle = '#3B82F6'; ctx.lineWidth = 2; ctx.beginPath(); ctx.roundRect(80, y1, trainL, 40, 8); ctx.stroke();
        ctx.fillStyle = '#3B82F6'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Train at rest', 80 + trainL / 2, y1 + 25);

        // L₀ bracket
        ctx.strokeStyle = '#3B82F6'; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.moveTo(80, y1 - 8); ctx.lineTo(80, y1 - 16); ctx.lineTo(400, y1 - 16); ctx.lineTo(400, y1 - 8); ctx.stroke();
        ctx.fillStyle = '#3B82F6'; ctx.font = 'bold 13px Inter,sans-serif';
        ctx.fillText('L₀ = 100 m', 240, y1 - 22);
        ctx.fillStyle = '#94A3B8'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'right';
        ctx.fillText('v = 0', 70, y1 + 25);

        // Moving at 0.87c
        const y2 = 160, contractedL = trainL / 2; // γ≈2 at 0.87c
        ctx.fillStyle = '#FEF3C7'; ctx.beginPath(); ctx.roundRect(80, y2, contractedL, 40, 8); ctx.fill();
        ctx.strokeStyle = '#F59E0B'; ctx.lineWidth = 2; ctx.beginPath(); ctx.roundRect(80, y2, contractedL, 40, 8); ctx.stroke();
        ctx.fillStyle = '#F59E0B'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Moving at 0.87c', 80 + contractedL / 2, y2 + 25);

        // L bracket
        ctx.strokeStyle = '#F59E0B'; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.moveTo(80, y2 - 8); ctx.lineTo(80, y2 - 16); ctx.lineTo(80 + contractedL, y2 - 16); ctx.lineTo(80 + contractedL, y2 - 8); ctx.stroke();
        ctx.fillStyle = '#F59E0B'; ctx.font = 'bold 13px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('L = L₀/γ ≈ 50 m', 80 + contractedL / 2, y2 - 22);
        ctx.fillStyle = '#94A3B8'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('v = 0.87c, γ ≈ 2', 15, y2 + 22);

        // Speed arrow
        ctx.fillStyle = '#F59E0B'; ctx.font = 'bold 16px Inter,sans-serif'; ctx.textAlign = 'left';
        ctx.fillText('→→→', 80 + contractedL + 10, y2 + 25);

        // Ghost of original length
        ctx.strokeStyle = '#CBD5E1'; ctx.lineWidth = 1; ctx.setLineDash([4, 4]);
        ctx.beginPath(); ctx.roundRect(80, y2, trainL, 40, 8); ctx.stroke();
        ctx.setLineDash([]);

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Objects contract along the direction of motion  -  only significant near c', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-amber-50 to-blue-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={240} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 4.5 – Energy-Momentum Triangle */
export const EnergyMassTriangle = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 520, H = 300;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Energy-Momentum Relationship', W / 2, 24);

        // Right triangle
        const ax = 120, ay = 230, bx = 380, by = 230, cx2 = 380, cy2 = 80;

        // Fill triangle
        ctx.fillStyle = '#EFF6FF';
        ctx.beginPath(); ctx.moveTo(ax, ay); ctx.lineTo(bx, by); ctx.lineTo(cx2, cy2); ctx.closePath(); ctx.fill();

        // Triangle sides
        ctx.lineWidth = 3;
        // Base: mc²
        ctx.strokeStyle = '#22C55E'; ctx.beginPath(); ctx.moveTo(ax, ay); ctx.lineTo(bx, by); ctx.stroke();
        // Height: pc
        ctx.strokeStyle = '#3B82F6'; ctx.beginPath(); ctx.moveTo(bx, by); ctx.lineTo(cx2, cy2); ctx.stroke();
        // Hypotenuse: E
        ctx.strokeStyle = '#EF4444'; ctx.beginPath(); ctx.moveTo(ax, ay); ctx.lineTo(cx2, cy2); ctx.stroke();

        // Right angle marker
        ctx.strokeStyle = '#94A3B8'; ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.moveTo(bx - 15, by); ctx.lineTo(bx - 15, by - 15); ctx.lineTo(bx, by - 15); ctx.stroke();

        // Labels
        ctx.font = 'bold 16px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillStyle = '#EF4444'; ctx.save(); ctx.translate((ax + cx2) / 2 - 20, (ay + cy2) / 2);
        ctx.rotate(Math.atan2(cy2 - ay, cx2 - ax)); ctx.fillText('E (Total Energy)', 0, -12); ctx.restore();
        ctx.fillStyle = '#22C55E'; ctx.fillText('mc² (Rest Energy)', (ax + bx) / 2, ay + 25);
        ctx.fillStyle = '#3B82F6'; ctx.textAlign = 'left'; ctx.fillText('pc', bx + 10, (by + cy2) / 2 + 5);

        // Formula
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 16px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('E² = (pc)² + (mc²)²', W / 2, ay + 50);

        // Special cases
        ctx.fillStyle = '#64748B'; ctx.font = '12px Inter,sans-serif';
        ctx.fillText('Photon (m=0): E = pc     |     At rest (p=0): E = mc²', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-red-50 to-green-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={520} height={300} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 4.6 – Relativity of Simultaneity */
export const SimultaneityTrain = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 300;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Relativity of Simultaneity', W / 2, 24);

        // Scene: Platform view (top)
        const py = 100;
        ctx.fillStyle = '#E2E8F0'; ctx.fillRect(40, py + 20, 480, 6); // platform

        // Train
        ctx.fillStyle = '#DBEAFE'; ctx.beginPath(); ctx.roundRect(120, py - 20, 320, 36, 8); ctx.fill();
        ctx.strokeStyle = '#3B82F6'; ctx.lineWidth = 2; ctx.beginPath(); ctx.roundRect(120, py - 20, 320, 36, 8); ctx.stroke();
        ctx.fillStyle = '#3B82F6'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('→ Train moving →', 280, py);

        // Lightning strikes at both ends (simultaneous for platform)
        ctx.fillStyle = '#FCD34D'; ctx.font = 'bold 16px Inter,sans-serif';
        ctx.fillText('⚡', 120, py - 30); ctx.fillText('⚡', 440, py - 30);

        // Platform observer
        ctx.fillStyle = '#64748B'; ctx.beginPath(); ctx.arc(280, py + 40, 10, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = '#64748B'; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(280, py + 50); ctx.lineTo(280, py + 62); ctx.stroke();
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 11px Inter,sans-serif';
        ctx.fillText('Platform observer: both strikes SIMULTANEOUS', 280, py + 78);

        // Scene: Train view (bottom)
        const ty = 210;
        ctx.fillStyle = '#E2E8F0'; ctx.fillRect(40, ty + 20, 480, 6);
        ctx.fillStyle = '#FEF3C7'; ctx.beginPath(); ctx.roundRect(120, ty - 20, 320, 36, 8); ctx.fill();
        ctx.strokeStyle = '#F59E0B'; ctx.lineWidth = 2; ctx.beginPath(); ctx.roundRect(120, ty - 20, 320, 36, 8); ctx.stroke();
        ctx.fillStyle = '#F59E0B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Train (observer rides along)', 280, ty);

        // Lightning: front arrives first because train moves toward it
        ctx.fillStyle = '#FCD34D'; ctx.font = 'bold 16px Inter,sans-serif';
        ctx.fillText('⚡', 440, ty - 30);
        ctx.fillStyle = '#FCD34D80'; ctx.fillText('⚡', 120, ty - 30);

        // Train observer
        ctx.fillStyle = '#F59E0B'; ctx.beginPath(); ctx.arc(280, ty + 40, 10, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = '#F59E0B'; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(280, ty + 50); ctx.lineTo(280, ty + 62); ctx.stroke();
        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 11px Inter,sans-serif';
        ctx.fillText('Train observer: front strike arrives FIRST', 280, ty + 78);

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('Events simultaneous in one frame are not simultaneous in another', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-amber-50 to-slate-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={300} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 4.10 – Relativity Effects Summary Table */
export const RelativityEffectsTable = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 300;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Special Relativity Effects at a Glance', W / 2, 24);

        const cols = [20, 160, 300, 440];
        const colW = [140, 140, 140, 100];
        const headers = ['Effect', 'Formula', 'At β = 0.87', 'At β = 0.99'];
        const rows = [
            ['Time Dilation', "Δt' = γΔt", '2.0 �- Δt', '7.1 �- Δt'],
            ['Length Contract.', "L = L₀/γ", 'L₀ / 2.0', 'L₀ / 7.1'],
            ['Rel. Momentum', 'p = γmv', '2.0 �- mv', '7.1 �- mv'],
            ['Total Energy', 'E = γmc²', '2.0 mc²', '7.1 mc²'],
        ];
        const rowColors = ['#3B82F6', '#22C55E', '#F59E0B', '#EF4444'];

        const hY = 52, rowH = 45;

        // Header row
        ctx.fillStyle = '#1E293B'; ctx.beginPath(); ctx.roundRect(15, hY, W - 30, 30, [8, 8, 0, 0]); ctx.fill();
        headers.forEach((h, i) => {
            ctx.fillStyle = 'white'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(h, cols[i] + colW[i] / 2, hY + 20);
        });

        // Data rows
        rows.forEach((row, ri) => {
            const y = hY + 30 + ri * rowH;
            ctx.fillStyle = ri % 2 === 0 ? '#F8FAFC' : 'white';
            ctx.fillRect(15, y, W - 30, rowH);
            ctx.strokeStyle = '#E2E8F0'; ctx.lineWidth = 1;
            ctx.beginPath(); ctx.moveTo(15, y + rowH); ctx.lineTo(W - 15, y + rowH); ctx.stroke();

            // Color bar
            ctx.fillStyle = rowColors[ri]; ctx.fillRect(15, y, 4, rowH);

            row.forEach((cell, ci) => {
                ctx.fillStyle = ci === 0 ? '#1E293B' : '#475569';
                ctx.font = ci === 0 ? 'bold 12px Inter,sans-serif' : '12px Inter,sans-serif';
                ctx.textAlign = 'center';
                ctx.fillText(cell, cols[ci] + colW[ci] / 2, y + rowH / 2 + 4);
            });
        });

        // Bottom note
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('γ(0.87c) ≈ 2.0   |   γ(0.99c) ≈ 7.1   |   All effects vanish at low speed (γ → 1)', W / 2, H - 12);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-indigo-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={300} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};
